import { useState, useEffect, useRef, useCallback, useMemo, FormEvent } from "react";
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowRight01Icon as ArrowRight, Cancel01Icon as X, CheckmarkSquare01Icon as Check, Key01Icon as Key, LockIcon as Lock, Mail01Icon as Mail, UserIcon as User, ViewIcon as Eye, ViewOffIcon as EyeOff } from '@hugeicons/core-free-icons';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import CONFIG from "@/config/env";

// ── Turnstile ───────────────────────────────────────────────────────────────
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, any>) => string;
      reset: (id: string) => void;
      remove: (id: string) => void;
    };
  }
}
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";

// Identify the just-logged-in user in InsightFlare immediately. The provider's
// auto-sync also catches this, but the browser's `storage` event does not fire in
// the same tab that wrote the value, so we identify explicitly here too.
function identifyInInsightFlare(data: any, fallbackEmail: string) {
  try {
    const username = data?.username;
    const email = data?.user?.email || fallbackEmail;
    const userId = username || email;
    const userName = data?.user?.name || username || email || "User";
    if (userId && typeof window !== "undefined" && window.insightflare) {
      window.insightflare.identify(String(userId), { name: String(userName) });
    }
  } catch {
    /* analytics must never block auth */
  }
}

function useTurnstileScript() {
  const [ready, setReady] = useState(!TURNSTILE_SITE_KEY || !!window.turnstile);
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || window.turnstile) { setReady(true); return; }
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    s.async = true; s.defer = true;
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, []);
  return ready;
}

function TurnstileWidget({ onToken, onExpire, widgetRef }: {
  onToken: (t: string) => void;
  onExpire: () => void;
  widgetRef: React.MutableRefObject<string | null>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptReady = useTurnstileScript();

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !scriptReady || !containerRef.current || widgetRef.current) return;
    try {
      widgetRef.current = window.turnstile!.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: onToken,
        "expired-callback": onExpire,
        "error-callback": onExpire,
        theme: "dark",
        size: "normal",
      });
    } catch (e) { console.error("[Turnstile]", e); }
    return () => {
      if (widgetRef.current && window.turnstile) {
        window.turnstile.remove(widgetRef.current);
        widgetRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptReady]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div className="flex justify-center" ref={containerRef} />;
}

type Mode = "login" | "signup" | "reset" | "verify" | "2fa" | "cli";

export default function Auth({ mode = "login" }: { mode?: Mode }) {
  const [isLogin, setIsLogin] = useState<Mode>(mode);
  const [isMobile, setIsMobile] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [sending, setSending] = useState(false);
  const [session, setSession] = useState<string | null>(null);

  // Terms & Privacy acceptance (signup only)
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  // Turnstile tokens
  const [signupTurnstileToken, setSignupTurnstileToken] = useState<string | null>(null);
  const [loginTurnstileToken, setLoginTurnstileToken] = useState<string | null>(null);
  const signupWidgetRef = useRef<string | null>(null);
  const loginWidgetRef = useRef<string | null>(null);

  const resetSignupTurnstile = useCallback(() => {
    setSignupTurnstileToken(null);
    if (signupWidgetRef.current && window.turnstile) window.turnstile.reset(signupWidgetRef.current);
  }, []);
  const resetLoginTurnstile = useCallback(() => {
    setLoginTurnstileToken(null);
    if (loginWidgetRef.current && window.turnstile) window.turnstile.reset(loginWidgetRef.current);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const apiBase = CONFIG.API_BASE;
  const getReturnTo = useCallback(() => {
    const qs = new URLSearchParams(location.search);
    const fromQuery = qs.get("returnTo");
    const fromSession = sessionStorage.getItem("genofx:returnTo");
    const candidate = fromQuery || fromSession || '';
    if (!candidate) return '/';
    // Allow absolute URLs to genofx subdomains
    if (candidate.startsWith('https://') && candidate.includes('.genofx.com')) {
      return candidate;
    }
    if (!candidate.startsWith('/')) return '/';
    if (candidate.startsWith('/auth')) return '/';
    return candidate;
  }, [location.search]);

  const redirectAfterAuth = useCallback((token: string) => {
    const adminUrl = CONFIG.ADMIN_URL;
    const returnTo = getReturnTo();
    sessionStorage.removeItem("genofx:returnTo");

    const target = new URL(returnTo, adminUrl);
    target.searchParams.set('token', token);
    window.location.href = target.toString();
  }, [getReturnTo]);

  // Guard: Redirect already-authenticated users to dashboard
  useEffect(() => {
    const token = localStorage.getItem("genofx:token");
    if (token) {
      navigate(getReturnTo(), { replace: true });
    }
  }, [navigate, getReturnTo]);

  const passwordRules = useMemo(() => [
    { label: "At least 8 characters", test: (pw: string) => pw.length >= 8 },
    { label: "At least one uppercase letter", test: (pw: string) => /[A-Z]/.test(pw) },
    { label: "At least one lowercase letter", test: (pw: string) => /[a-z]/.test(pw) },
    { label: "At least one number", test: (pw: string) => /[0-9]/.test(pw) },
    { label: "At least one special character", test: (pw: string) => /[!@#$%^&*(),.?":{}|<> ]/.test(pw) },
  ], []);

  const isPasswordValid = useMemo(() =>
    passwordRules.every(rule => rule.test(password)),
    [password, passwordRules]);

  const handleGoogleAuth = async () => {
    try {
      const qs = new URLSearchParams(location.search);
      const invite = qs.get("invite");
      const returnTo = getReturnTo();
      sessionStorage.setItem("genofx:returnTo", returnTo);
      const url = new URL(`${apiBase}/api/auth/google/url`);
      if (invite) url.searchParams.set("invite", invite);
      if (returnTo && returnTo !== '/') url.searchParams.set("returnTo", returnTo);

      const response = await fetch(url.toString());
      
      if (!response.ok) {
        toast.error(`Failed to initiate Google auth (${response.status})`);
        console.error("Failed to get Google auth URL:", response.statusText);
        return;
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Google auth URL not available. Please try again.");
        console.error("No auth URL in response:", data);
      }
    } catch (error) {
      toast.error("Failed to connect to Google auth. Please try again.");
      console.error("Failed to get Google auth URL:", error);
    }
  };

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = (e: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in e ? e.matches : (e as MediaQueryList).matches;
      setIsMobile(matches);
    };
    update(mq);
    const handler = (e: MediaQueryListEvent) => update(e);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    // @ts-ignore
    setShowWelcome(isMobile);
  }, [isMobile]);

  useEffect(() => {
    setIsLogin(mode);
  }, [mode]);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const presetEmail = qs.get("email") || "";
    if (presetEmail) setEmail(presetEmail);
  }, [location.search]);

  const handleSignup = async (e: FormEvent) => {
    e.preventDefault();
    setSending(true);
    setStatus(null);

    if (!acceptedTerms || !acceptedPrivacy) {
      setStatus({ text: "Please accept the Terms of Service and Privacy Policy to continue.", type: "error" });
      setSending(false);
      return;
    }

    if (TURNSTILE_SITE_KEY && !signupTurnstileToken) {
      setStatus({ text: "Please complete the security check.", type: "error" });
      setSending(false);
      return;
    }

    const qs = new URLSearchParams(location.search);
    const invite = qs.get("invite");

    try {
      const response = await fetch(`${apiBase}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, username, invite, turnstileToken: signupTurnstileToken }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || "Registration successful! Please verify your email.");
        setIsLogin("verify");
      } else {
        if (data.unverified) {
          toast.info(data.message);
          setIsLogin("verify");
          resetSignupTurnstile();
          return;
        }
        toast.error(data.message || "Signup failed");
        setStatus({ text: data.message || "Signup failed", type: "error" });
        resetSignupTurnstile();
      }
    } catch (_) {
      toast.error("Server error during signup");
      setStatus({ text: "Server error during signup", type: "error" });
      resetSignupTurnstile();
    } finally {
      setSending(false);
    }
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setSending(true);
    setStatus(null);

    if (TURNSTILE_SITE_KEY && !loginTurnstileToken) {
      setStatus({ text: "Please complete the security check.", type: "error" });
      setSending(false);
      return;
    }

    try {
      const response = await fetch(`${apiBase}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, turnstileToken: loginTurnstileToken }),
      });
      const data = await response.json();
      if (response.ok) {
        if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
          toast.info("New password required. Please reset your password.");
          setStatus({ text: "New password required. Please use the reset password flow.", type: "error" });
          setIsLogin("reset");
          return;
        }
        if (data.challengeName === 'SOFTWARE_TOKEN_MFA') {
          setSession(data.session);
          setIsLogin("2fa");
          toast.info("Please enter your 2FA code");
          return;
        }
        localStorage.setItem("genofx:token", data.token);
        // Set shared cookie for cross-subdomain auth (ads.genofx.com, apps.genofx.com)
        document.cookie = `genofx_token=${data.token}; domain=.genofx.com; path=/; max-age=${60*60*24*30}; secure; samesite=lax`;
        if (data.accessToken) {
          localStorage.setItem("genofx:accessToken", data.accessToken);
        }
        if (data.user?.sessionId || data.sessionId) {
          localStorage.setItem("genofx:sessionId", data.user?.sessionId || data.sessionId);
        }
        localStorage.setItem("genofx:refreshToken", data.refreshToken);
        localStorage.setItem("genofx:username", data.username);
        localStorage.setItem("genofx:user", JSON.stringify(data.user));
        localStorage.setItem("genofx:email", data.user?.email || email);
        if (data.user?.avatar_url) {
          localStorage.setItem("profile:photo", data.user.avatar_url);
        }
        identifyInInsightFlare(data, email);
        toast.success("Login successful!");
        redirectAfterAuth(data.token);
      } else {
        if (data.unverified) {
          toast.info(data.message);
          setIsLogin("verify");
          return;
        }
        toast.error(data.message || "Login failed");
        setStatus({ text: data.message || "Login failed", type: "error" });
        resetLoginTurnstile();
      }
    } catch (_) {
      toast.error("Server error during login");
      setStatus({ text: "Server error during login", type: "error" });
      resetLoginTurnstile();
    } finally {
      setSending(false);
    }
  };

  const renderForm = () => {
    if (isLogin === "cli") {
      return (
        <div className="space-y-6">
          <button
            className="w-full inline-flex items-center justify-center gap-2.5 h-11 px-4 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-white text-sm font-medium transition-colors"
            onClick={handleGoogleAuth}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width='1em' height='1em' className="h-5 w-5" aria-hidden="true"><path fill="#fff" d="M44.59 4.21a63.28 63.28 0 0 0 4.33 120.9a67.6 67.6 0 0 0 32.36.35a57.13 57.13 0 0 0 25.9-13.46a57.44 57.44 0 0 0 16-26.26a74.33 74.33 0 0 0 1.61-33.58H65.27v24.69h34.47a29.72 29.72 0 0 1-12.66 19.52a36.16 36.16 0 0 1-13.93 5.5a41.29 41.29 0 0 1-15.1 0A37.16 37.16 0 0 1 44 95.74a39.3 39.3 0 0 1-14.5-19.42a38.31 38.31 0 0 1 0-24.63a39.25 39.25 0 0 1 9.18-14.91A37.17 37.17 0 0 1 76.13 27a34.28 34.28 0 0 1 13.64 8q5.83-5.8 11.64-11.63c2-2.09 4.18-4.08 6.15-6.22A61.22 61.22 0 0 0 87.2 4.59a64 64 0 0 0-42.61-.38z" /><path fill="#e33629" d="M44.59 4.21a64 64 0 0 1 42.61.37a61.22 61.22 0 0 1 20.35 12.62c-2 2.14-4.11 4.14-6.15 6.22Q95.58 29.23 89.77 35a34.28 34.28 0 0 0-13.64-8a37.17 37.17 0 0 0-37.46 9.74a39.25 39.25 0 0 0-9.18 14.91L8.76 35.6A63.53 63.53 0 0 1 44.59 4.21z" /><path fill="#f8bd00" d="M3.26 51.5a62.93 62.93 0 0 1 5.5-15.9l20.73 16.09a38.31 38.31 0 0 0 0 24.63q-10.36 8-20.73 16.08a63.33 63.33 0 0 1-5.5-40.9z" /><path fill="#587dbd" d="M65.27 52.15h59.52a74.33 74.33 0 0 1-1.61 33.58a57.44 57.44 0 0 1-16 26.26c-6.69-5.22-13.41-10.4-20.1-15.62a29.72 29.72 0 0 0 12.66-19.54H65.27c-.01-8.22 0-16.45 0-24.68z" /><path fill="#319f43" d="M8.75 92.4q10.37-8 20.73-16.08A39.3 39.3 0 0 0 44 95.74a37.16 37.16 0 0 0 14.08 6.08a41.29 41.29 0 0 0 15.1 0a36.16 36.16 0 0 0 13.93-5.5c6.69 5.22 13.41 10.4 20.1 15.62a57.13 57.13 0 0 1-25.9 13.47a67.6 67.6 0 0 1-32.36-.35a63 63 0 0 1-23-11.59A63.73 63.73 0 0 1 8.75 92.4z" /></svg>
            <span>Continue with Google</span>
          </button>

          <div className="flex items-center gap-3 w-full">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-white/30 uppercase tracking-wider">Or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium text-white/80">Email</label>
              <div className="relative group">
                <HugeiconsIcon icon={Mail} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
                <Input
                  id="email"
                  type="email"
                  className="w-full h-11 pl-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all outline-none ring-0 focus-visible:ring-0"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@genofx.com"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="password" className="text-sm font-medium text-white/80">Password</label>
              <div className="relative group">
                <HugeiconsIcon icon={Lock} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="w-full h-11 pl-10 pr-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all outline-none ring-0 focus-visible:ring-0"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors" onClick={() => setShowPassword((v) => !v)}>
                  {showPassword ? <HugeiconsIcon icon={EyeOff} className="h-4 w-4"  /> : <HugeiconsIcon icon={Eye} className="h-4 w-4"  />}
                </button>
              </div>
            </div>

            {/* Turnstile widget for CLI login */}
            <TurnstileWidget
              onToken={setLoginTurnstileToken}
              onExpire={() => setLoginTurnstileToken(null)}
              widgetRef={loginWidgetRef}
            />

            <Button type="submit" className="w-full h-11 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-all shadow-sm mt-2" disabled={sending || (!!TURNSTILE_SITE_KEY && !loginTurnstileToken)}>
              {sending ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </div>
      );
    }

    if (isLogin === "signup") {
      return (
        <form onSubmit={handleSignup} className="space-y-4">
          <div className="space-y-1">
            <label htmlFor="username" className="text-sm font-medium text-white/80">Username</label>
            <div className="relative group">
              <HugeiconsIcon icon={User} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
              <Input
                id="username"
                type="text"
                className="w-full h-11 pl-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all outline-none ring-0 focus-visible:ring-0"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="johndoe"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label htmlFor="email" className="text-sm font-medium text-white/80">Email address</label>
            <div className="relative group">
              <HugeiconsIcon icon={Mail} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
              <Input
                id="email"
                type="email"
                className="w-full h-11 pl-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all outline-none ring-0 focus-visible:ring-0"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="name@example.com"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label htmlFor="password" className="text-sm font-medium text-white/80">Password</label>
            <div className="relative group">
              <HugeiconsIcon icon={Lock} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                className="w-full h-11 pl-10 pr-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all outline-none ring-0 focus-visible:ring-0"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors" onClick={() => setShowPassword((v) => !v)}>
                {showPassword ? <HugeiconsIcon icon={EyeOff} className="h-4 w-4"  /> : <HugeiconsIcon icon={Eye} className="h-4 w-4"  />}
              </button>
            </div>
            <div className="grid grid-cols-1 gap-1.5 mt-2">
              {passwordRules.map((rule, i) => {
                const isMet = rule.test(password);
                return (
                  <div key={i} className={`flex items-center gap-2 text-[10px] transition-colors ${isMet ? 'text-green-500' : 'text-white/40'}`}>
                    {isMet ? <HugeiconsIcon icon={Check} className="h-3 w-3"  /> : <div className="h-1.5 w-1.5 rounded-full bg-white/20" />}
                    <span>{rule.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Terms & Privacy checkboxes */}
          <div className="space-y-2.5">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 accent-[#6E1AF4] cursor-pointer shrink-0"
              />
              <span className="text-xs text-white/50 group-hover:text-white/70 transition-colors leading-relaxed">
                I agree to the{" "}
                <a href="https://genofx.com/terms" target="_blank" rel="noopener noreferrer" className="text-[#a78bfa] hover:text-[#c4b5fd] underline underline-offset-2">
                  Terms of Service
                </a>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 accent-[#6E1AF4] cursor-pointer shrink-0"
              />
              <span className="text-xs text-white/50 group-hover:text-white/70 transition-colors leading-relaxed">
                I have read and accept the{" "}
                <a href="https://genofx.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#a78bfa] hover:text-[#c4b5fd] underline underline-offset-2">
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>

          {/* Turnstile widget */}
          <TurnstileWidget
            onToken={setSignupTurnstileToken}
            onExpire={() => setSignupTurnstileToken(null)}
            widgetRef={signupWidgetRef}
          />

          <Button type="submit" className="w-full h-11 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-all shadow-sm" disabled={sending || !isPasswordValid || !acceptedTerms || !acceptedPrivacy || (!!TURNSTILE_SITE_KEY && !signupTurnstileToken)}>
            {sending ? "Creating account..." : "Sign up"}
          </Button>
          <p className="text-center text-sm text-white/40">
            Already have an account? <Link to="/auth/login" className="text-[#a78bfa] hover:text-[#c4b5fd] transition-colors font-medium">Log In</Link>
          </p>
        </form>
      );
    }

    if (isLogin === "login") {
      return (
        <form onSubmit={handleLogin} className="space-y-5">
          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm font-medium text-white/80">Email</label>
            <div className="relative group">
              <HugeiconsIcon icon={Mail} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
              <Input
                id="email"
                type="email"
                className="w-full h-11 pl-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all outline-none ring-0 focus-visible:ring-0"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@genofx.com"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="text-sm font-medium text-white/80">Password</label>
              <Link to="/auth/reset" className="text-xs text-[#a78bfa] hover:text-[#c4b5fd] transition-colors">Forgot password?</Link>
            </div>
            <div className="relative group">
              <HugeiconsIcon icon={Lock} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                className="w-full h-11 pl-10 pr-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all outline-none ring-0 focus-visible:ring-0"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors" onClick={() => setShowPassword((v) => !v)}>
                {showPassword ? <HugeiconsIcon icon={EyeOff} className="h-4 w-4"  /> : <HugeiconsIcon icon={Eye} className="h-4 w-4"  />}
              </button>
            </div>
          </div>

          {/* Turnstile widget for login */}
          <TurnstileWidget
            onToken={setLoginTurnstileToken}
            onExpire={() => setLoginTurnstileToken(null)}
            widgetRef={loginWidgetRef}
          />

          <Button type="submit" className="w-full h-11 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-all shadow-sm mt-2" disabled={sending || (!!TURNSTILE_SITE_KEY && !loginTurnstileToken)}>
            {sending ? "Signing in..." : "Sign in"}
          </Button>

          <p className="text-center text-sm text-white/40 mt-4">
            <Link to="/auth/signup" className="text-[#a78bfa] hover:text-[#c4b5fd] transition-colors font-medium">First time here? Sign up</Link>
          </p>
        </form>
      );
    }

    if (isLogin === "reset") {
      return (
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-white/80">Email address</label>
            <div className="flex gap-2">
              <div className="relative flex-1 group">
                <HugeiconsIcon icon={Mail} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
                <Input className="w-full h-11 pl-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Enter your email" />
              </div>
              <Button type="button" className="h-11 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white border border-white/10" onClick={async () => {
                setSending(true);
                setStatus(null);
                try {
                  const r = await fetch(`${apiBase}/api/auth/forgot`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
                  const j = await r.json();
                  if (r.ok) toast.success("Reset code sent! Check your email.");
                  else toast.error(j.message || "Failed to send code");
                  setStatus({ text: j.message || 'Code sent if the email exists.', type: r.ok ? "success" : "error" });
                } catch {
                  toast.error("Failed to send code");
                  setStatus({ text: 'Failed to send code', type: "error" });
                } finally { setSending(false); }
              }} disabled={sending || !email}>
                {sending ? '...' : 'Send Code'}
              </Button>
            </div>
          </div>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setSending(true);
            setStatus(null);
            const form = e.target as HTMLFormElement;
            const codeInput = form.elements.namedItem('code') as HTMLInputElement | null;
            const codeVal = codeInput?.value || '';
            try {
              const r = await fetch(`${apiBase}/api/auth/reset/code`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, code: codeVal, password }) });
              const j = await r.json();
              if (r.ok) {
                toast.success("Password reset successful! Please log in.");
                setIsLogin("login");
              } else {
                toast.error(j.message || "Reset failed");
              }
              setStatus({ text: j.message || (r.ok ? 'Password updated' : 'Invalid code'), type: r.ok ? "success" : "error" });
            } catch {
              toast.error("Reset failed");
              setStatus({ text: 'Reset failed', type: "error" });
            } finally { setSending(false); }
          }} className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-white/80">Verification code</label>
              <div className="relative group">
                <HugeiconsIcon icon={Key} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
                <Input name="code" className="w-full h-11 pl-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all font-mono tracking-widest" placeholder="000000" required />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-white/80">New password</label>
              <div className="relative group">
                <HugeiconsIcon icon={Lock} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
                <Input className="w-full h-11 pl-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="New password" />
              </div>
            </div>

            <Button type="submit" className="w-full h-11 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-all shadow-sm" disabled={sending || !isPasswordValid}>Reset Password</Button>

            <button
              type="button"
              className="w-full text-center text-sm text-[#a78bfa] hover:text-[#c4b5fd] transition-colors mt-2"
              onClick={() => setIsLogin('login')}
            >
              Back to Login
            </button>
          </form>
        </div>
      );
    }

    if (isLogin === "2fa") {
      return (
        <div className="space-y-6">
          <div className="text-sm text-white/60 text-center">
            Enter your 6-digit authenticator code or a backup code to continue.
          </div>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setSending(true);
            setStatus(null);

            const form = e.target as HTMLFormElement;
            const codeInput = form.elements.namedItem('code') as HTMLInputElement | null;
            const codeVal = codeInput?.value || '';

            try {
              const r = await fetch(`${apiBase}/api/auth/login/2fa`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, session, code: codeVal })
              });

              const data = await r.json();

              if (r.ok) {
                localStorage.setItem("genofx:token", data.token);
                document.cookie = `genofx_token=${data.token}; domain=.genofx.com; path=/; max-age=${60*60*24*30}; secure; samesite=lax`;
                if (data.accessToken) localStorage.setItem("genofx:accessToken", data.accessToken);
                if (data.user?.sessionId || data.sessionId) localStorage.setItem("genofx:sessionId", data.user?.sessionId || data.sessionId);
                localStorage.setItem("genofx:refreshToken", data.refreshToken);
                localStorage.setItem("genofx:username", data.username);
                localStorage.setItem("genofx:user", JSON.stringify(data.user));
                localStorage.setItem("genofx:email", data.user?.email || email);
                if (data.user?.avatar_url) localStorage.setItem("profile:photo", data.user.avatar_url);

                identifyInInsightFlare(data, email);
                toast.success("Login successful!");
                redirectAfterAuth(data.token);
              } else {
                toast.error(data.message || "Verification failed");
                setStatus({ text: data.message || 'Verification failed', type: "error" });
              }
            } catch (error) {
              console.error("2FA error:", error);
              toast.error("Verification failed");
              setStatus({ text: 'Verification failed', type: "error" });
            } finally {
              setSending(false);
            }
          }} className="space-y-4">
            <div className="space-y-1">
              <div className="relative group">
                <HugeiconsIcon icon={Key} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
                <Input
                  name="code"
                  className="w-full h-11 pl-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all text-center text-lg tracking-[0.5em] font-mono"
                  placeholder="000000 or ABCDE-FGHIJ"
                  required
                  autoFocus
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-11 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-all shadow-sm"
              disabled={sending}
            >
              {sending ? "Verifying..." : "Verify Identity"}
            </Button>

            <button
              type="button"
              className="w-full text-center text-sm text-[#a78bfa] hover:text-[#c4b5fd] transition-colors"
              onClick={() => setIsLogin('login')}
            >
              Back to Login
            </button>
          </form>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-sm text-white/60 text-center">
          Enter the verification code sent to <br /><strong className="text-white">{email}</strong>
        </div>
        <form onSubmit={async (e) => {
          e.preventDefault();
          setSending(true);
          setStatus(null);
          const form = e.target as HTMLFormElement;
          const codeInput = form.elements.namedItem('code') as HTMLInputElement | null;
          const codeVal = codeInput?.value || '';
          try {
            const r = await fetch(`${apiBase}/api/auth/verify/code`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email, code: codeVal })
            });
            const j = await r.json();
            if (r.ok) {
              toast.success("Email verified successfully! You can now log in.");
              setIsLogin('login');
              navigate('/auth/login');
            } else {
              toast.error(j.message || "Verification failed");
            }
            setStatus({ text: j.message || (r.ok ? 'Verified' : 'Invalid code'), type: r.ok ? "success" : "error" });
          } catch {
            toast.error("Verification failed");
            setStatus({ text: 'Verification failed', type: "error" });
          } finally { setSending(false); }
        }} className="space-y-4">
          <div className="space-y-1">
            <div className="relative group">
              <HugeiconsIcon icon={Key} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 group-focus-within:text-[#6E1AF4] transition-colors"  />
              <Input
                name="code"
                className="w-full h-11 pl-10 rounded-lg border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#6E1AF4] focus:bg-[#6E1AF4]/5 transition-all text-center text-lg tracking-[0.5em] font-mono"
                placeholder="000000"
                required
                autoFocus
              />
            </div>
          </div>
          <Button
            type="submit"
            className="w-full h-11 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-all shadow-sm"
            disabled={sending}
          >
            {sending ? "Verifying..." : "Verify Account"}
          </Button>

          <div className="flex flex-col gap-3 pt-2">
            <button
              type="button"
              className="text-xs text-white/40 hover:text-white transition-colors"
              onClick={async () => {
                setSending(true);
                try {
                  const r = await fetch(`${apiBase}/api/auth/resend-code`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                  });
                  const j = await r.json();
                  if (r.ok) toast.success("A new code has been sent to your email.");
                  else toast.error(j.message || "Failed to resend code");
                } catch {
                  toast.error("Failed to resend code");
                } finally { setSending(false); }
              }}
              disabled={sending}
            >
              Didn't receive a code? Resend
            </button>
            <button
              type="button"
              className="text-sm text-[#a78bfa] hover:text-[#c4b5fd] transition-colors"
              onClick={() => setIsLogin('login')}
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div className="min-h-dvh grid md:grid-cols-2 overflow-hidden bg-black font-sans selection:bg-[#6E1AF4]/30">
      <div
        className="hidden md:block h-dvh w-full relative bg-cover bg-center pointer-events-none"
        aria-hidden="true"
        role="presentation"
        style={{
          backgroundImage: `url('${isLogin === "cli" ? "/cli.png" : "/login.png"}'), radial-gradient(ellipse at 50% 50%, rgba(124, 58, 237, 0.15) 0%, rgba(0,0,0,0) 70%)`,
          backgroundSize: "cover, 100% 100%",
          backgroundPosition: "center, center",
        }}
      >
        <div className="absolute inset-0 bg-black/30" />
        {/* Header text */}
        <div className="absolute top-10 left-10 z-10 max-w-[400px]">
          <h2 className="text-3xl md:text-4xl lg:text-[40px] font-semibold text-white leading-[1.15] drop-shadow-lg uppercase tracking-wider">
            &ldquo;LET US HANDLE
            <br />
            THE HURDLE,
            <br />
            SO YOU SCALE
            <br />
            WHAT MATTERS&rdquo;
          </h2>
        </div>
      </div>

      <div className="flex items-start md:items-center justify-center min-h-dvh w-full p-6 lg:p-12 overflow-y-auto bg-black relative">
        {/* Glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#6E1AF4]/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="w-full max-w-[400px] py-6 md:py-10 relative z-10 flex flex-col min-h-full md:min-h-0 justify-start md:justify-center">

          <div className="text-center mb-10 space-y-3">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#6E1AF4]/10 text-[#6E1AF4] mb-2 border border-[#6E1AF4]/20 overflow-hidden">
              <img src="/favicon.ico" alt="GenofX" className="h-7 w-7 object-contain" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-white">
              {isLogin === "cli" ? "GenofX CLI" : "GenofX"}
            </h1>
            <p className="text-white/40 text-sm">
              {isLogin === "cli" ? "Command Line Interface" : isLogin === "login" ? "Welcome back" : isLogin === "signup" ? "Create an account" : isLogin === "reset" ? "Reset your password" : "Verify your identity"}
            </p>
          </div>

          <div className="space-y-6">
            {/* Google Auth - Only show on Login/Signup */}
            {(isLogin === "login" || isLogin === "signup") && (
              <>
                <button
                  className="w-full inline-flex items-center justify-center gap-2.5 h-11 px-4 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-white text-sm font-medium transition-colors"
                  onClick={handleGoogleAuth}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width='1em' height='1em' className="h-5 w-5" aria-hidden="true"><path fill="#fff" d="M44.59 4.21a63.28 63.28 0 0 0 4.33 120.9a67.6 67.6 0 0 0 32.36.35a57.13 57.13 0 0 0 25.9-13.46a57.44 57.44 0 0 0 16-26.26a74.33 74.33 0 0 0 1.61-33.58H65.27v24.69h34.47a29.72 29.72 0 0 1-12.66 19.52a36.16 36.16 0 0 1-13.93 5.5a41.29 41.29 0 0 1-15.1 0A37.16 37.16 0 0 1 44 95.74a39.3 39.3 0 0 1-14.5-19.42a38.31 38.31 0 0 1 0-24.63a39.25 39.25 0 0 1 9.18-14.91A37.17 37.17 0 0 1 76.13 27a34.28 34.28 0 0 1 13.64 8q5.83-5.8 11.64-11.63c2-2.09 4.18-4.08 6.15-6.22A61.22 61.22 0 0 0 87.2 4.59a64 64 0 0 0-42.61-.38z" /><path fill="#e33629" d="M44.59 4.21a64 64 0 0 1 42.61.37a61.22 61.22 0 0 1 20.35 12.62c-2 2.14-4.11 4.14-6.15 6.22Q95.58 29.23 89.77 35a34.28 34.28 0 0 0-13.64-8a37.17 37.17 0 0 0-37.46 9.74a39.25 39.25 0 0 0-9.18 14.91L8.76 35.6A63.53 63.53 0 0 1 44.59 4.21z" /><path fill="#f8bd00" d="M3.26 51.5a62.93 62.93 0 0 1 5.5-15.9l20.73 16.09a38.31 38.31 0 0 0 0 24.63q-10.36 8-20.73 16.08a63.33 63.33 0 0 1-5.5-40.9z" /><path fill="#587dbd" d="M65.27 52.15h59.52a74.33 74.33 0 0 1-1.61 33.58a57.44 57.44 0 0 1-16 26.26c-6.69-5.22-13.41-10.4-20.1-15.62a29.72 29.72 0 0 0 12.66-19.54H65.27c-.01-8.22 0-16.45 0-24.68z" /><path fill="#319f43" d="M8.75 92.4q10.37-8 20.73-16.08A39.3 39.3 0 0 0 44 95.74a37.16 37.16 0 0 0 14.08 6.08a41.29 41.29 0 0 0 15.1 0a36.16 36.16 0 0 0 13.93-5.5c6.69 5.22 13.41 10.4 20.1 15.62a57.13 57.13 0 0 1-25.9 13.47a67.6 67.6 0 0 1-32.36-.35a63 63 0 0 1-23-11.59A63.73 63.73 0 0 1 8.75 92.4z" /></svg>
                  <span>Continue with Google</span>
                </button>

                <div className="flex items-center gap-3 w-full">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-xs text-white/30 uppercase tracking-wider">Or</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
              </>
            )}

            {status && (
              <div className={cn(
                "text-sm text-center p-3 rounded-lg border backdrop-blur-sm",
                status.type === "success"
                  ? "bg-green-500/10 border-green-500/20 text-green-400"
                  : "bg-red-500/10 border-red-500/20 text-red-400"
              )}>
                {status.text}
              </div>
            )}

            {renderForm()}

          </div>

          <div className="mt-12 text-center">
            {/* Footer Content if needed */}
          </div>
        </div>
      </div>
    </div>
  );
}
